"use client";

import { ArrowRightIcon, CheckIcon, XIcon, ZoomInIcon } from "lucide-react";
import { motion } from "framer-motion";
import Link from "next/link";
import { createPortal } from "react-dom";

import {
    fitScreenshotToAppSize,
    getScreenshotCardStyles,
    useFeatureImageZoomAction,
    fitScreenshotIntoCard,
    fitLargeScreenshotIntoCard,
} from "./useFeatureImageZoomAction";

import { getFeatureShowcaseContent } from "./feature-showcase-content";
import { getFeatureShowcaseBlocks } from "./feature-showcase-images";
import { getFeatureShowcaseMore } from "./feature-showcase-more";

const revealProps = {
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, amount: 0.2 },
    transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] },
};

// "Learn more" style link to the matching help center article.
function FeatureLearnMoreLink({ href, label }) {
    if (!href) return null;
    return (
        <Link
            href={href}
            className="group mt-6 inline-flex items-center gap-1.5 text-[15px] font-semibold text-fe-primary hover:underline"
        >
            {label || "Learn how it works"}
            <ArrowRightIcon className="size-4 transition-transform group-hover:translate-x-1" />
        </Link>
    );
}

// Full-screen view of one screenshot, so every word is readable.
export function FeatureImageZoom({ src, alt, onClose }) {
    return createPortal(
        <div
            role="dialog"
            aria-modal="true"
            aria-label={alt}
            onClick={onClose}
            className="fixed inset-0 z-[100] flex cursor-zoom-out items-center justify-center bg-black/80 p-4 backdrop-blur-sm sm:p-8"
        >
            <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full bg-white/90 text-neutral-900 shadow hover:bg-white"
            >
                <XIcon className="size-5" />
            </button>
            <img
                src={src}
                alt={alt}
                className="max-h-full max-w-full rounded-xl bg-white object-contain shadow-2xl"
            />
        </div>,
        document.body,
    );
}

export function EnlargeHint({ style }) {
    return (
        <span
            style={style}
            className="pointer-events-none absolute flex items-center gap-1.5 rounded-full bg-black/70 px-3 py-1.5 text-xs font-medium text-white opacity-80 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100"
        >
            <ZoomInIcon className="size-3.5" />
            Click to enlarge
        </span>
    );
}

// Full-screen kiosk screenshot as a clean rounded card (Mangomint-style),
// no device bezel.
export function TabletFrame({ children, style }) {
    return (
        <span
            style={style}
            className="block overflow-hidden rounded-3xl bg-white shadow-[0_30px_70px_rgba(10,27,33,0.18)]"
        >
            {children}
        </span>
    );
}

// Real screenshot over a landing photo (Vagaro-style).
// With row.layout: a small card showing part of the topic, placed in a
// corner or the middle, over a clear or blurred photo.
// Without it (hero): the whole screenshot fitted in the middle.
// Click either to see the whole screenshot.
export function FeatureScreenshotPanel({ row, aspectClassName = "aspect-[4/3]" }) {
    const { isOpen, open, close } = useFeatureImageZoomAction();
    const layout = row.layout;
    const card = layout && !row.tablet ? getScreenshotCardStyles(layout.position) : null;
    const plainBackground = layout ? row.plainBackground : null;
    const blurred = !plainBackground && (layout ? layout.blur : true);

    return (
        <>
            {/* Plain and clear-photo panels paint their background on the panel
                itself (not a layer on top), so no dark base shows as a thin
                ring at the rounded corners. Only blurred photos need the base. */}
            <div
                style={
                    plainBackground
                        ? { background: plainBackground }
                        : !blurred
                          ? { backgroundImage: `url(${row.background})` }
                          : undefined
                }
                className={`relative isolate ${aspectClassName} w-full overflow-hidden rounded-3xl ${
                    plainBackground
                        ? ""
                        : blurred
                          ? "bg-[#0a1b21] shadow-[0_24px_60px_rgba(10,27,33,0.12)]"
                          : "bg-cover bg-center shadow-[0_24px_60px_rgba(10,27,33,0.12)]"
                }`}
            >
                {blurred && (
                    <div
                        aria-hidden="true"
                        className="absolute inset-0 scale-110 bg-cover bg-center blur-[6px]"
                        style={{ backgroundImage: `url(${row.background})` }}
                    />
                )}
                {blurred && <div aria-hidden="true" className="absolute inset-0 bg-black/15" />}

                {row.screenshot && card && (
                    <button
                        type="button"
                        onClick={open}
                        aria-label={`Enlarge screenshot: ${row.title}`}
                        style={card.frame}
                        className="group absolute inset-0 flex cursor-zoom-in"
                    >
                        <span
                            style={card.card}
                            className={`block overflow-hidden bg-white ${
                                plainBackground
                                    ? "rounded-2xl border border-black/5 p-2.5 sm:p-3"
                                    : "rounded-xl border border-white/60 shadow-[0_20px_50px_rgba(0,0,0,0.35)]"
                            }`}
                        >
                            <img
                                ref={plainBackground ? fitLargeScreenshotIntoCard : fitScreenshotIntoCard}
                                src={row.screenshot}
                                alt={row.title}
                                loading="lazy"
                                className={`block h-auto max-w-none ${plainBackground ? "rounded-lg" : ""}`}
                            />
                        </span>
                        <EnlargeHint style={card.pill} />
                    </button>
                )}

                {row.screenshot && row.tablet && (
                    <button
                        type="button"
                        onClick={open}
                        aria-label={`Enlarge screenshot: ${row.title}`}
                        style={{ padding: "7%", containerType: "size" }}
                        className="group absolute inset-0 flex cursor-zoom-in items-center justify-center"
                    >
                        {/* Kiosk shots are 4:3 (iPad landscape), so the frame keeps that shape. */}
                        <TabletFrame style={{ width: "min(100cqw, 133cqh)" }}>
                            <img src={row.screenshot} alt={row.title} loading="lazy" className="block h-auto w-full" />
                        </TabletFrame>
                        <EnlargeHint style={{ right: "4%", bottom: "4%" }} />
                    </button>
                )}

                {row.screenshot && !card && !row.tablet && (
                    <button
                        type="button"
                        onClick={open}
                        aria-label={`Enlarge screenshot: ${row.title}`}
                        style={{ inset: "7%", containerType: "size" }}
                        className="group absolute flex cursor-zoom-in flex-col items-center justify-center-safe overflow-hidden rounded-xl"
                    >
                        {/* Smaller than the panel so the background photo shows around it.
                            Tall screenshots show their top part; click shows all of it. */}
                        <img
                            ref={fitScreenshotToAppSize}
                            src={row.screenshot}
                            alt={row.title}
                            loading="lazy"
                            className="h-auto max-w-full rounded-xl border border-white/60 bg-white shadow-[0_20px_50px_rgba(0,0,0,0.35)]"
                        />
                        <EnlargeHint style={{ right: "0.75rem", bottom: "0.75rem" }} />
                    </button>
                )}
            </div>
            {isOpen && <FeatureImageZoom src={row.screenshot} alt={row.title} onClose={close} />}
        </>
    );
}

function FeatureShowcaseRow({ row, index }) {
    // Even rows: image left, text right. Odd rows: text left, image right.
    const imageRight = index % 2 === 1;

    return (
        <motion.div
            {...revealProps}
            className="grid items-center gap-10 md:grid-cols-2 lg:gap-20"
        >
            <div className={imageRight ? "md:order-2" : ""}>
                <FeatureScreenshotPanel row={row} />
            </div>

            <div className={imageRight ? "md:order-1" : ""}>
                {row.badge && (
                    <span className="mb-4 inline-block rounded-full bg-neutral-100 px-3 py-1.5 text-[13px] font-semibold tracking-wider text-neutral-900">
                        {row.badge}
                    </span>
                )}
                <h3 className="text-3xl font-semibold leading-tight text-neutral-900 md:text-[2.25rem]">
                    {row.title}
                </h3>
                <p className="mt-5 text-base leading-relaxed text-neutral-600">
                    {row.description}
                </p>
                {row.items?.length > 0 && (
                    <ul className="mt-6 space-y-3">
                        {row.items.map((item, i) => (
                            <li key={`${row.key}-item-${i}`} className="flex items-start gap-3">
                                <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-fe-primary text-white">
                                    <CheckIcon className="size-3.5" />
                                </span>
                                <span className="text-base leading-6 text-neutral-800">{item}</span>
                            </li>
                        ))}
                    </ul>
                )}
                <FeatureLearnMoreLink href={row.href} label={row.linkLabel} />
            </div>
        </motion.div>
    );
}

// Centered heading with two image cards side by side (Vagaro-style group).
function FeatureShowcaseGroup({ group }) {
    return (
        <motion.div {...revealProps}>
            <div className="mx-auto mb-12 max-w-3xl text-center">
                <h3 className="font-display text-3xl font-extrabold leading-tight tracking-tight text-[#0a1b21] md:text-[2.75rem]">
                    {group.heading}
                </h3>
                {group.intro && (
                    <p className="mt-4 text-lg text-neutral-600">{group.intro}</p>
                )}
            </div>
            <div className="grid gap-12 md:grid-cols-2 md:gap-8">
                {group.cards.map((card) => (
                    <div key={card.title}>
                        <FeatureScreenshotPanel row={card} />
                        <h4 className="mt-6 text-xl font-semibold text-neutral-900">{card.title}</h4>
                        <p className="mt-3 text-base leading-relaxed text-neutral-600">{card.description}</p>
                        <FeatureLearnMoreLink href={card.href} label={card.linkLabel} />
                    </div>
                ))}
            </div>
        </motion.div>
    );
}

export function FeatureShowcaseSection({ slug, feature }) {
    const showcase = getFeatureShowcaseContent(slug, feature);
    const blocks = getFeatureShowcaseBlocks(
        slug,
        showcase.tabs,
        feature,
        getFeatureShowcaseMore(slug),
    );

    return (
        <section className="relative overflow-hidden bg-white pb-24 pt-16 md:pb-32 md:pt-24">
            <div className="relative mx-auto max-w-7xl px-6">
                <div className="mx-auto max-w-4xl text-center mb-16 md:mb-24">
                    <motion.h2
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="font-display text-4xl font-extrabold leading-[1.1] tracking-tight text-[#0a1b21] md:text-[3.5rem]"
                    >
                        {(() => {
                            if (!showcase.heading) return null;
                            const words = showcase.heading.split(" ");
                            if (words.length < 2) return showcase.heading;

                            return (
                                <>
                                    {words[0]}{" "}
                                    <span className="relative inline-block">
                                        <span className="relative z-10">{words[1]}</span>
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            viewBox="0 0 337 12"
                                            fill="none"
                                            preserveAspectRatio="none"
                                            className="absolute -bottom-2 h-3.5 left-0 w-full text-fe-primary"
                                        >
                                            <path
                                                d="M2.02157 6.67507C59.3392 4.51664 116.939 4.73302 174.328 3.84915C225.628 3.05906 276.93 2.75644 328.24 2.36882C330.362 2.35278 336.552 1.98241 334.603 2.56927C328.79 4.31962 317.08 3.46496 311.52 3.62073C234.971 5.76542 158.303 4.80472 81.7124 6.00586C62.8847 6.30113 6.44803 10.8458 25.2248 9.86957C49.2136 8.62234 73.5295 6.28546 97.5684 5.90297C146.166 5.12971 194.761 5.00185 243.365 4.52532C254.575 4.41541 265.8 4.20438 277.013 4.2801"
                                                stroke="currentColor"
                                                strokeWidth={3.5}
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            />
                                        </svg>
                                    </span>{" "}
                                    {words.slice(2).join(" ")}
                                </>
                            );
                        })()}
                    </motion.h2>
                    {showcase.intro && (
                        <p className="mx-auto mt-10 max-w-2xl text-lg text-neutral-600">
                            {showcase.intro}
                        </p>
                    )}
                </div>

                <div className="flex flex-col gap-24 md:gap-32">
                    {blocks.map((block) =>
                        block.type === "group" ? (
                            <FeatureShowcaseGroup key={block.key} group={block} />
                        ) : (
                            <FeatureShowcaseRow key={block.key} row={block} index={block.rowIndex} />
                        ),
                    )}
                </div>
            </div>
        </section>
    );
}
